import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLeading } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { storageActions } from "./slices";
import {
  CreateSnapshotParams,
  CreateSnapshotResponse,
  CreateVolumeParams,
  CreateVolumeResponse,
  DeleteSnapshotParams,
  DeleteSnapshotResponse,
  DeleteVolumeParams,
  DeleteVolumeResponse,
  DeleteVolumesParams,
  DeleteVolumesResponse,
  ExtendVolumeParams,
  ExtendVolumeResponse,
  GetSnapshotParams,
  GetSnapshotResponse,
  GetSnapshotsParams,
  GetSnapshotsResponse,
  GetVolumeLimitsParams,
  GetVolumeLimitsResponse,
  GetVolumeParams,
  GetVolumeResponse,
  GetVolumeTypeParams,
  GetVolumeTypeResponse,
  GetVolumeTypesParams,
  GetVolumeTypesResponse,
  GetVolumesParams,
  GetVolumesResponse,
  UpdateSnapshotParams,
  UpdateSnapshotResponse,
  UpdateVolumeParams,
  UpdateVolumeResponse
} from "./types";

export function* getVolumeSaga(
  action: PayloadAction<GetVolumeParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetVolumeResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/volumes/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getVolumeSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get volume data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getVolumesSaga(
  action: PayloadAction<GetVolumesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetVolumesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/volumes`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getVolumesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getVolumesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get volumes data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createVolumeSaga(
  action: PayloadAction<CreateVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateVolumeResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-volume/method/${projectId}/volumes`,
      data
    );
    yield put(storageActions.createVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Volume has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.createVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateVolumeSaga(
  action: PayloadAction<UpdateVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateVolumeResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-volume/method/${projectId}/volumes/${id}`,
      data
    );
    yield put(storageActions.updateVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Volume has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.updateVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* extendVolumeSaga(
  action: PayloadAction<ExtendVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Extending the volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<ExtendVolumeResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-volume/method/${projectId}/volumes/${id}`,
      data
    );
    yield put(storageActions.extendVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Volume has been successfully extended.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.extendVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to extend volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteVolumeSaga(
  action: PayloadAction<DeleteVolumeParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete volume...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteVolumeResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-volume/method/${projectId}/volumes/${id}`
    );
    yield put(storageActions.deleteVolumeSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete volume has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.deleteVolumeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteVolumesSaga(
  action: PayloadAction<DeleteVolumesParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete volumes...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<DeleteVolumesResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-volume/method/${projectId}/volumes/multiple/delete`,
      { volume_ids: data }
    );

    const entries = Object.entries(response.data);

    const formattedEntries = entries.map(
      ([volumeId, Message]: [string, string]) => {
        const MessageWithoutDetails = Message.replace(/\. .*$/, "");
        return `${volumeId} - ${MessageWithoutDetails}`;
      }
    );

    const { deletedVolumes, failedToDeleteVolumes } = formattedEntries.reduce(
      (result, entry) => {
        if (entry.includes("deleted")) {
          result.deletedVolumes.push(entry);
        } else {
          result.failedToDeleteVolumes.push(entry);
        }
        return result;
      },
      { deletedVolumes: [] as string[], failedToDeleteVolumes: [] as string[] }
    );

    const resultText = `${deletedVolumes.join("\n")}\n${
      failedToDeleteVolumes.length > 0
        ? `${failedToDeleteVolumes.join(
            "\n"
          )}\n\nNotice: You cannot delete volumes that are in reserved or in-use status, migrating, attached, belong to a group, have associated snapshots, or need to be disassociated from snapshots after a volume transfer.`
        : ""
    }`;

    yield put(storageActions.deleteVolumesSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: `Open to see results...`,
        text: `${resultText}`,
        type: NOTIFICATION_TYPES.INFO
      })
    );
  } catch (e) {
    yield put(
      storageActions.deleteVolumesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete volume",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getVolumeLimitsSaga(
  action: PayloadAction<GetVolumeLimitsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetVolumeLimitsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/volumes/limits`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getVolumeLimitsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getVolumeLimitsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get volume limits data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSnapshotSaga(
  action: PayloadAction<GetSnapshotParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetSnapshotResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/snapshots/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getSnapshotSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getSnapshotFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get snapshot data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSnapshotsSaga(
  action: PayloadAction<GetSnapshotsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetSnapshotsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/snapshots`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getSnapshotsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getSnapshotsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get snapshots data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createSnapshotSaga(
  action: PayloadAction<CreateSnapshotParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the snapshot...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateSnapshotResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-volume/method/${projectId}/snapshots`,
      data
    );
    yield put(storageActions.createSnapshotSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Snapshot has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.createSnapshotFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create snapshot",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateSnapshotSaga(
  action: PayloadAction<UpdateSnapshotParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the snapshot...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpdateSnapshotResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-volume/method/${projectId}/snapshots/${id}`,
      data
    );
    yield put(storageActions.updateSnapshotSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Snapshot has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.updateSnapshotFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update snapshot",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteSnapshotSaga(
  action: PayloadAction<DeleteSnapshotParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete snapshot...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteSnapshotResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-volume/method/${projectId}/snapshots/${id}`
    );
    yield put(storageActions.deleteSnapshotSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete snapshot has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      storageActions.deleteSnapshotFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete snapshot",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getVolumeTypeSaga(
  action: PayloadAction<GetVolumeTypeParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetVolumeTypeResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/volumetypes/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getVolumeTypeSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getVolumeTypeFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get volume type data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getVolumeTypesSaga(
  action: PayloadAction<GetVolumeTypesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetVolumeTypesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-volume/method/${projectId}/volumetypes`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(storageActions.getVolumeTypesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      storageActions.getVolumeTypesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get volume types data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(storageActions.getVolumeRequest, getVolumeSaga),
    takeLeading(storageActions.getVolumesRequest, getVolumesSaga),
    takeEvery(storageActions.createVolumeRequest, createVolumeSaga),
    takeEvery(storageActions.updateVolumeRequest, updateVolumeSaga),
    takeEvery(storageActions.extendVolumeRequest, extendVolumeSaga),
    takeEvery(storageActions.deleteVolumeRequest, deleteVolumeSaga),
    takeEvery(storageActions.deleteVolumesRequest, deleteVolumesSaga),
    takeLeading(storageActions.getVolumeLimitsRequest, getVolumeLimitsSaga),
    takeLeading(storageActions.getSnapshotRequest, getSnapshotSaga),
    takeLeading(storageActions.getSnapshotsRequest, getSnapshotsSaga),
    takeEvery(storageActions.createSnapshotRequest, createSnapshotSaga),
    takeEvery(storageActions.updateSnapshotRequest, updateSnapshotSaga),
    takeEvery(storageActions.deleteSnapshotRequest, deleteSnapshotSaga),
    takeLeading(storageActions.getVolumeTypeRequest, getVolumeTypeSaga),
    takeLeading(storageActions.getVolumeTypesRequest, getVolumeTypesSaga)
  ]);
}
